import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, Search, Sparkles } from 'lucide-react';

import { useDialogA11y } from '@/hooks/useDialogA11y';
import { usePermissions } from '@/hooks/usePermissions';
import { useTranslation } from '@/lib/i18n';
import { composeNav } from '@/components/layout/nav-compose';
import type { NavItem } from '@/components/layout/nav-registry';
import { SETTINGS_SECTIONS, canSeeSection } from '@/pages/admin/settings/sections';
import { useGlobalSearch, type SearchHit } from '@/api/queries/search';
import { pushRecent, readRecents, useDebounced } from './useCommandPalette';

/**
 * Palette de commandes — ⌘K.
 *
 * Deux sortes d'entrées, jamais mélangées dans une liste indifférenciée
 * (« Don't mix records, actions, and navigation ») :
 *  - NAVIGATION : les destinations du menu, filtrées par EXACTEMENT le même
 *    `composeNav` que la barre latérale. Une palette qui proposerait une
 *    page que le menu cache dirait à l'utilisateur qu'elle existe.
 *  - DONNÉES : clients, articles, fournisseurs… via /api/search, dont la
 *    portée par permissions est appliquée PAR LE SERVEUR.
 *
 * Accessibilité (WAI-ARIA « combobox » + « listbox ») : le champ porte
 * `role="combobox"`, la liste `role="listbox"`, chaque entrée
 * `role="option"`, et `aria-activedescendant` désigne la ligne courante —
 * le focus RESTE dans le champ pour que la frappe continue, ce qui est la
 * raison d'être de ce motif plutôt qu'un déplacement de focus réel.
 */

interface PaletteEntry {
  /** Stable across renders and sessions — also the recents key. */
  key: string;
  label: string;
  hint?: string;
  href: string;
  group: string;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /** Hands the typed question to the AI chat (⌘J), from within the palette. */
  onAskAi?: ((question: string) => void) | undefined;
  /** Same filtering inputs the sidebar receives — one source of truth. */
  navInput: Omit<Parameters<typeof composeNav>[0], 'hasPermission' | 'hasAnyPermission'>;
}

export function CommandPalette({ open, onClose, onAskAi, navInput }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [recents, setRecents] = useState<string[]>([]);

  const dialogRef = useDialogA11y<HTMLDivElement>({ open, onClose, initialFocus: inputRef });

  // 300 ms: long enough that a typed word is one request, short enough that
  // the list feels attached to the keyboard.
  const debounced = useDebounced(query, 300);
  const { data, isFetching } = useGlobalSearch(debounced, { enabled: open });

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setRecents(readRecents());
    }
  }, [open]);

  /**
   * Navigation destinations, flattened out of the same tree as the sidebar.
   * The settings entry brings the sections its rail shows, filtered like
   * the rail (SettingsLayout): the section's permission and the
   * jurisdiction's surfaces, hidden only once known to be absent. A section
   * the menu already opens through an entry of its own is listed once.
   */
  const navEntries = useMemo<PaletteEntry[]>(() => {
    const groups = composeNav({ ...navInput, hasPermission, hasAnyPermission });
    const menuHrefs = new Set(
      groups.flatMap((group) => (group.items as NavItem[]).map((item) => item.href))
    );
    const entries: PaletteEntry[] = [];
    for (const group of groups) {
      const groupLabel = group.labelKey ? t(group.labelKey as never) : 'Navigation';
      for (const item of group.items as NavItem[]) {
        if (!item.href || item.heading) continue;
        entries.push({
          key: `nav:${item.key}`,
          label: t(item.labelKey as never),
          href: item.href,
          group: groupLabel,
        });
        if (item.key !== 'settings') continue;
        for (const section of SETTINGS_SECTIONS) {
          if (!canSeeSection(section, hasPermission, hasAnyPermission)) continue;
          if (section.surface !== undefined && navInput.surfaces?.[section.surface] === false) {
            continue;
          }
          const href = `${item.href}/${section.path}`;
          if (menuHrefs.has(href)) continue;
          entries.push({
            key: `nav:settings:${section.path}`,
            label: t(section.labelKey),
            hint: t(item.labelKey as never),
            href,
            group: groupLabel,
          });
        }
      }
    }
    return entries;
  }, [navInput, hasPermission, hasAnyPermission, t]);

  const matchedNav = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) {
      // Empty palette: recents first, then a short head of the menu — never
      // a blank panel.
      const byKey = new Map(navEntries.map((e) => [e.key, e]));
      const recent = recents
        .map((key) => byKey.get(key))
        .filter((e): e is PaletteEntry => e !== undefined)
        .map((e) => ({ ...e, group: 'Récent' }));
      const rest = navEntries.filter((e) => !recents.includes(e.key)).slice(0, 6);
      return [...recent, ...rest];
    }
    return navEntries
      .filter((entry) => entry.label.toLowerCase().includes(term))
      .slice(0, 8);
  }, [navEntries, query, recents]);

  const dataEntries = useMemo<PaletteEntry[]>(() => {
    const hits = (data?.results ?? []) as SearchHit[];
    const groupOf: Record<SearchHit['type'], string> = {
      client: 'Clients',
      article: 'Articles',
      supplier: 'Fournisseurs',
      employee: 'Personnel',
      project: 'Projets',
      invoice: 'Factures',
    };
    return hits.map((hit) => ({
      key: `${hit.type}:${hit.id}`,
      label: hit.label,
      ...(hit.hint ? { hint: hit.hint } : {}),
      href: hit.href,
      group: groupOf[hit.type],
    }));
  }, [data]);

  const entries = useMemo(
    () => [...matchedNav, ...dataEntries],
    [matchedNav, dataEntries]
  );

  // The active row must never point past the list after it shrinks.
  useEffect(() => {
    setActiveIndex((index) => (index >= entries.length ? 0 : index));
  }, [entries.length]);

  const select = useCallback(
    (entry: PaletteEntry) => {
      if (entry.key.startsWith('nav:')) setRecents(pushRecent(entry.key));
      onClose();
      navigate(entry.href);
    },
    [navigate, onClose]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((i) => (entries.length === 0 ? 0 : (i + 1) % entries.length));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((i) =>
          entries.length === 0 ? 0 : (i - 1 + entries.length) % entries.length
        );
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const entry = entries[activeIndex];
        if (entry) select(entry);
        else if (onAskAi && query.trim()) {
          // Zero results is not a dead end: the question goes to the AI.
          onClose();
          onAskAi(query.trim());
        }
      }
    },
    [entries, activeIndex, select, onAskAi, query, onClose]
  );

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    const active = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!open) return null;

  let lastGroup = '';
  const rows = entries.map((entry, index) => {
    const heading = entry.group !== lastGroup ? entry.group : null;
    lastGroup = entry.group;
    const isActive = index === activeIndex;
    return (
      <div key={entry.key}>
        {heading && (
          <div
            role="presentation"
            className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500"
          >
            {heading}
          </div>
        )}
        <div
          id={`palette-option-${index}`}
          role="option"
          aria-selected={isActive}
          data-active={isActive}
          onClick={() => select(entry)}
          onMouseEnter={() => setActiveIndex(index)}
          className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-lg ${
            isActive ? 'bg-brand-lagune-50 text-brand-lagune-900' : 'text-slate-700'
          }`}
        >
          <span className="flex-1 truncate text-sm">{entry.label}</span>
          {entry.hint && (
            <span
              className={`text-xs truncate max-w-[35%] ${isActive ? 'text-slate-600' : 'text-slate-500'}`}
            >
              {entry.hint}
            </span>
          )}
          {isActive && <CornerDownLeft className="w-3.5 h-3.5 text-brand-lagune-600 shrink-0" />}
        </div>
      </div>
    );
  });

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[110]" onClick={onClose} />
      <div className="fixed inset-0 z-[110] flex items-start justify-center pt-[12vh] px-4 pointer-events-none">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="Palette de commandes"
          className="pointer-events-auto w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
        >
          <div className="flex items-center gap-3 px-4 border-b border-slate-100">
            <Search className="w-4 h-4 text-slate-400 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls="palette-listbox"
              aria-autocomplete="list"
              {...(entries.length > 0
                ? { 'aria-activedescendant': `palette-option-${activeIndex}` }
                : {})}
              aria-label="Rechercher une page, un client, un article"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Aller à une page, chercher un client, un article…"
              className="flex-1 h-12 text-sm outline-none placeholder:text-slate-500"
            />
            <kbd className="text-[10px] text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
              Échap
            </kbd>
          </div>

          <div
            ref={listRef}
            id="palette-listbox"
            role="listbox"
            aria-label="Résultats"
            className="max-h-[52vh] overflow-y-auto p-2"
          >
            {entries.length > 0 ? (
              rows
            ) : (
              <div className="px-3 py-8 text-center">
                <p className="text-sm text-slate-500">
                  {isFetching ? 'Recherche…' : `Aucun résultat pour « ${query} »`}
                </p>
                {onAskAi && query.trim() && !isFetching && (
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onAskAi(query.trim());
                    }}
                    className="mt-3 inline-flex items-center gap-2 px-3 py-2 text-sm text-brand-lagune-700 bg-brand-lagune-50 rounded-lg hover:bg-brand-lagune-100"
                  >
                    <Sparkles className="w-4 h-4" />
                    Poser la question à l'assistant
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Announced to screen readers only — the sighted user sees the list. */}
          <div aria-live="polite" className="sr-only">
            {isFetching
              ? 'Recherche en cours'
              : `${entries.length} résultat${entries.length > 1 ? 's' : ''}`}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
